import React, { useState, useEffect } from 'react';
import { Grid, Radio, RadioGroup, Typography, makeStyles, Divider } from '@material-ui/core';
import LensIcon from '@material-ui/icons/Lens';
import KeyboardArrowLeftRounded from '@material-ui/icons/KeyboardArrowLeftRounded';
import KeyboardArrowRightRounded from '@material-ui/icons/KeyboardArrowRightRounded';
import { Button } from '@swagup-com/components';
import { useDispatch, useSelector } from 'react-redux';
import ScrollMenu from 'react-horizontal-scrolling-menu';
import { Modal } from '../../shared/Modal';
import Counter from '../../shared/Counter';
import Img from '../../shared/Img';
import QuantitySelect from './QuantitySelect';
import {
  addProductToCart,
  removeProductFromCart,
  updateProductInCart,
  changeNumberOfColors
} from '../../../actions/cart';
import catalogApi from '../../../apis/swagup/catalog';
import { moneyStr } from '../../../helpers/utils';
import { resize } from '../../../utils/images';
import styles, { availableColorsStyles } from './ProductDetailsModal.styles';
import { useAuth } from '../../global/Authentication/AuthProvider';

const useStyles = makeStyles(styles);
const useAvailableColorsStyles = makeStyles(availableColorsStyles);

const AvailableColors = ({ availableColors, colorIdx, onChange }) => {
  const classes = useAvailableColorsStyles();

  const [open, setOpen] = useState(false);

  const handleChange = event => onChange(+event.target.value);

  return availableColors.length ? (
    <>
      <Typography className={classes.title}>Select color</Typography>
      <div>
        <RadioGroup aria-label="color" value={colorIdx} onChange={handleChange} className={classes.colors}>
          {availableColors.slice(0, open ? undefined : 6).map(({ color }, idx) => (
            <Radio
              key={color}
              value={idx}
              icon={<LensIcon />}
              className={classes.colorButton}
              style={{ color: `#${color}` }}
            />
          ))}
          {availableColors.length > 6 && (
            <Button variant="text" size="small" onClick={() => setOpen(!open)} className={classes.expandButton}>
              {open ? 'Collapse' : `+${availableColors.length - 6}`}
            </Button>
          )}
        </RadioGroup>
      </div>
    </>
  ) : (
    <Typography className={classes.title}>No colors available</Typography>
  );
};

const ProductDetailsModal = ({ product, onClose }) => {
  const [quantitySelectOpen, setQuantitySelectOpen] = useState(false);

  const classes = useStyles();
  const { items: cart, pack, numberOfColors, presetQuantities, minQuantity } = useSelector(state => state.cart);
  const dispatch = useDispatch();

  const cartItem = cart.find(i => i.product.id === product.id);

  const [state, setState] = useState(
    cartItem || {
      colorIdx: null,
      quantity: pack ? 1 : 100,
      prices: (pack ? [pack.quantity] : presetQuantities).reduce((acc, q) => ({ ...acc, [q]: 0 }), {})
    }
  );

  const { colorIdx, quantity, prices } = state;

  useEffect(() => {
    if (cartItem) return;

    catalogApi
      .getPricing(
        [{ product, quantity }],
        numberOfColors,
        pack ? [pack.quantity] : presetQuantities,
        !pack,
        'product-details'
      )
      .then(pricing => setState({ ...state, prices: pricing.prices[product.id] }))
      .catch(() => {});
  }, [quantity, numberOfColors]);

  useEffect(() => {
    if (!cartItem) return;
    setState(cartItem);
  }, [cartItem]);

  const handleProductUpdate = (newQuantity, newColorIdx, updatePrices = true) => {
    const productUpdate = {
      quantity: newQuantity,
      colorIdx: newColorIdx,
      prices
    };

    setState(productUpdate);

    if (cartItem) dispatch(updateProductInCart(product.id, productUpdate, updatePrices));
  };

  const handleQuantityChange = newQuantity => {
    setQuantitySelectOpen(false);
    handleProductUpdate(newQuantity, colorIdx);
  };

  const handleColorIdxChange = newColorIdx => handleProductUpdate(quantity, newColorIdx, false);

  const handleNumberOfColorsChange = newNumberOfColors => dispatch(changeNumberOfColors(newNumberOfColors));

  const handleAddProduct = () => {
    dispatch(addProductToCart(product, quantity, colorIdx, prices));
    onClose();
  };

  const handleRemoveProduct = () => dispatch(removeProductFromCart(product));

  const currentColor = product.available_colors[colorIdx] || { image: product.img, label: 'Designer' };
  const price = prices[pack?.quantity || quantity] ?? 0;

  const { isAuthenticated, isPending } = useAuth();
  const isLoggedIn = !isPending && isAuthenticated;
  const currentMinQuantity = minQuantity.get(isLoggedIn);

  return (
    <Modal open onClose={onClose} classes={{ content: classes.root }}>
      <Grid container spacing={4}>
        <Grid item sm={6} xs={12}>
          <Img src={resize(currentColor.image, 690)} alt={product.name} className={classes.mainImage} />
          <ScrollMenu
            data={product.available_colors.map(({ image, label }, idx) => (
              <Img
                key={label}
                src={resize(image, 78)}
                onClick={() => handleColorIdxChange(idx)}
                className={classes.thumbnailImage}
              />
            ))}
            arrowLeft={<KeyboardArrowLeftRounded style={{ left: 0, zIndex: 999, cursor: 'pointer' }} />}
            arrowRight={<KeyboardArrowRightRounded style={{ right: 0, cursor: 'pointer' }} />}
            itemsCount={product.available_colors.length}
            selected={currentColor.label}
            scrollToSelected
            alignCenter
            alignOnResize
            hideArrows
            wheel={false}
            scrollBy={3}
            translate={15}
            itemClass={classes.thumbnailImageWrapper}
          />
        </Grid>
        <Grid item sm={6} xs={12} className={classes.info}>
          <div>
            <Typography className={classes.title}>{product.name}</Typography>
            <Typography className={classes.color}>{currentColor.label}</Typography>
          </div>
          <div>
            <AvailableColors
              availableColors={product.available_colors}
              colorIdx={colorIdx}
              onChange={handleColorIdxChange}
            />
          </div>
          {!pack && (
            <QuantitySelect
              value={quantity}
              quantities={presetQuantities}
              prices={prices}
              open={quantitySelectOpen}
              minQuantity={currentMinQuantity}
              onToggle={setQuantitySelectOpen}
              onChange={handleQuantityChange}
              className={classes.quantitySelect}
            />
          )}
          <div className={classes.quantities}>
            {pack && (
              <div>
                <div className={classes.quantityTitle}>
                  <Typography className={classes.label}>Quantity</Typography>
                  <Typography className={classes.labelMax}>(max 10)</Typography>
                </div>
                <Counter value={quantity} min={1} max={10} onChange={handleQuantityChange} />
              </div>
            )}
            <div>
              <Typography className={classes.label}>Number of colors in Logo</Typography>
              <Counter value={numberOfColors} min={1} max={4} onChange={handleNumberOfColorsChange} />
            </div>
          </div>
          <div>
            <Typography className={classes.label}>Price</Typography>
            <div className={classes.prices}>
              <Typography className={classes.totalPrice}> {moneyStr(price)}/item</Typography>
              <Typography data-testid="product-cost">{moneyStr(price * quantity * (pack?.quantity || 1))}</Typography>
              <Typography>{pack ? `${pack.quantity} packs` : `${quantity} orders`}</Typography>
            </div>
          </div>
          {cartItem ? (
            <Button variant="secondary" onClick={handleRemoveProduct} className={classes.button}>
              Remove from cart
            </Button>
          ) : (
            <Button variant="primary" onClick={handleAddProduct} className={classes.button}>
              Add to {pack ? 'Pack' : 'Order'}
            </Button>
          )}
          <Divider className={classes.divider} />
          <div>
            <Typography className={classes.label}>Description</Typography>
            <Typography className={classes.description}>{product.description}</Typography>
          </div>
        </Grid>
      </Grid>
    </Modal>
  );
};

export default ProductDetailsModal;
